import { useListUserResourcePermissions } from "@/queries/group-queries";
import { useMembership } from "@/hooks/use-membership";
import type { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import type { Permission } from "@/utils/permissions";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";

/**
 * Hook for checking user permissions on resources via group memberships.
 *
 * Provides a convenient API for checking if the current user has specific permissions
 * on resources. Organization admins have all permissions automatically.
 * Uses the prefetched permission data from AuthenticatedUserLayout.
 *
 * @example
 * ```tsx
 * const { hasPermission, isLoading, isOrgAdmin } = useResourcePermissions();
 *
 * if (isLoading) return <Loading />;
 *
 * const canUpdate = hasPermission(ResourceType.PROJECT, project.id, "project:update");
 * const canDelete = hasPermission(ResourceType.PROJECT, project.id, "project:delete");
 * ```
 *
 * @param enabled - Whether the query should run (default: true)
 * @returns Object with hasPermission function, loading state, and isOrgAdmin flag
 */
export const useResourcePermissions = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const query = useListUserResourcePermissions({ enabled });
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    /**
     * Check if the current user has a specific permission on a resource.
     * Organization admins always have all permissions.
     *
     * @param resourceType - The type of resource (PROJECT, RUNNER, etc.)
     * @param resourceId - The ID of the resource
     * @param permission - The permission to check (e.g., "project:update")
     * @returns true if user has the permission or is org admin, false otherwise
     */
    const hasPermission = (resourceType: ResourceType, resourceId: string, permission: Permission): boolean => {
        // Organization admins have all permissions
        if (isOrgAdmin) return true;

        if (!query.data?.groupPermissions) return false;
        const permissions = query.data.groupPermissions(resourceType, resourceId);
        return permissions.has(permission);
    };

    return {
        hasPermission,
        isLoading: query.isPending,
        isOrgAdmin,
    };
};
